import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { HOME_PATH, CANCER_DETECT_PATH } from '@/constants/paths';
import { useContextStore } from '@/hooks/context/useContextStore';
import { useLatestContext } from '@/hooks/context/useLatestContext';
import { useCancerDetectUser } from '@hooks/useCancerDetectUser';
import { getKitName } from '@utils/getKitName';

export const CONSUMER_VALUE = 'consumer';
export const CANCER_DETECT_VALUE = 'cancer-detect';
export const orgValue = (id: number) => `org-${id}`;

export interface ContextSwitcherOption {
  value: string;
  label: string;
  /** This context holds the most recent results (DE-702). */
  showLatest: boolean;
}

export interface ContextSwitcher {
  /** More than one experience exists, so a switcher is worth rendering. */
  canSwitch: boolean;
  options: ContextSwitcherOption[];
  currentValue: string;
  currentLabel: string;
  /** "Test Results" for Pro (org) users; "My Tests" for consumer kit toggle. */
  title: string;
  selectValue: (value: string) => void;
}

export interface UseContextSwitcherParams {
  /**
   * Also offer the consumer's wellness-kit ↔ CancerDetect toggle for users with
   * no partner org. The sidebar renders that pair as its own "My Tests" link
   * list, so only surfaces without one (mobile Profile) opt in.
   */
  includeConsumerKitToggle?: boolean;
}

/**
 * Shared state for the in-app experience switcher (DE-336) — the sidebar
 * dropdown on desktop and the Profile > Information row on mobile both read
 * from here so the two never disagree on what's switchable.
 */
export const useContextSwitcher = ({
  includeConsumerKitToggle = false,
}: UseContextSwitcherParams = {}): ContextSwitcher => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const consumerExists = useContextStore((s) => s.consumerExists);
  const consumerInit = useContextStore((s) => s.consumerInit);
  const orgs = useContextStore((s) => s.orgs);
  const patientOrg = useContextStore((s) => s.patientOrg);
  const selectConsumer = useContextStore((s) => s.selectConsumer);
  const selectOrg = useContextStore((s) => s.selectOrg);
  const { latestContextKey } = useLatestContext();
  const { isCancerDetectClicked, setIsCancerDetectClicked } =
    useCancerDetectUser();

  // The consumer payload can hold a wellness kit and/or CancerDetect. Detect
  // each by kit-id presence (kitDetails/CDkitDetails are `{...ids:null}` when
  // absent, not null), so a CD-only consumer isn't mistaken for a wellness one.
  const consumerHasWellnessKit = Boolean(
    consumerInit?.kitDetails?.kitId || consumerInit?.kitDetails?.PreRnrKitId,
  );
  const consumerHasCD = Boolean(
    consumerInit?.CDkitDetails?.kitId || consumerInit?.CDkitDetails?.PreRnrKitId,
  );

  // "Consumer" is only a real entry when the consumer experience has a wellness
  // kit. A CD-only consumer (e.g. Pro + CD) surfaces solely as the CancerDetect
  // entry — the "Consumer" row would be an empty duplicate of it.
  const showConsumer = consumerExists && consumerHasWellnessKit;
  // CancerDetect is folded into this switcher for Pro users (org context), and
  // for consumer-only users when the caller has no "My Tests" list of its own.
  // CD is a consumer product, so detect it from the consumer payload — NOT the
  // context-aware bootup, which drops CD while an org context is active.
  const showCancerDetect =
    consumerHasCD &&
    (orgs.length > 0 || (includeConsumerKitToggle && showConsumer));

  // Keep the persisted CD flag in sync with the resolved context: an org context
  // is never "in CD" (clear a stale flag, e.g. carried across a reload); a
  // CD-only consumer context always is (so the iframe's `cancer_detect_ui` param
  // and the CD-filtered sidebar nav match what's rendered).
  useEffect(() => {
    if (patientOrg) {
      if (isCancerDetectClicked) setIsCancerDetectClicked(false);
    } else if (showCancerDetect && !showConsumer && !isCancerDetectClicked) {
      setIsCancerDetectClicked(true);
    }
  }, [
    patientOrg,
    showCancerDetect,
    showConsumer,
    isCancerDetectClicked,
    setIsCancerDetectClicked,
  ]);

  // Alongside CancerDetect, "Consumer" reads as a second product rather than a
  // context, so that pairing names the actual wellness kit like the sidebar's
  // "My Tests" list does. Pro users keep the "Consumer" label (Test Results).
  const isProUser = orgs.length > 0;
  const consumerLabel =
    (includeConsumerKitToggle &&
      !isProUser &&
      (getKitName(consumerInit?.kitDetails?.kitType) ||
        getKitName(consumerInit?.kitDetails?.pre_rnr_kit_type))) ||
    'Consumer';

  const options: ContextSwitcherOption[] = [
    ...(showConsumer
      ? [
          {
            value: CONSUMER_VALUE,
            label: consumerLabel,
            // Latest badge is Pro-only (DE-702) — consumer kit toggle mirrors
            // the sidebar "My Tests" list, which has no badge.
            showLatest: isProUser && latestContextKey === CONSUMER_VALUE,
          },
        ]
      : []),
    ...orgs.map((entry) => ({
      value: orgValue(entry.org.id),
      label: entry.org.name,
      showLatest: isProUser && latestContextKey === entry.org.id,
    })),
    ...(showCancerDetect
      ? [
          {
            value: CANCER_DETECT_VALUE,
            label: 'CancerDetect',
            // CD belongs to the consumer context; when the Consumer row is
            // suppressed (CD-only consumer) the CD row carries its badge.
            showLatest:
              isProUser && !showConsumer && latestContextKey === CONSUMER_VALUE,
          },
        ]
      : []),
  ];

  // In consumer context (no org) a CD-only consumer resolves to CancerDetect,
  // since that's the only thing the consumer experience holds.
  const currentValue = patientOrg
    ? orgValue(patientOrg.id)
    : showCancerDetect && (isCancerDetectClicked || !showConsumer)
      ? CANCER_DETECT_VALUE
      : CONSUMER_VALUE;

  const selectValue = (value: string) => {
    if (value === currentValue) return;
    if (value === CANCER_DETECT_VALUE) {
      // CD lives on the consumer experience — activate it so the iframe's
      // token/kit scoping resolves against the CD kit, not the org's. No
      // consumer payload means no CD to show; bail rather than null the context.
      if (!consumerInit) return;
      selectConsumer(true);
      setIsCancerDetectClicked(true);
      queryClient.invalidateQueries();
      navigate(CANCER_DETECT_PATH);
      return;
    }
    setIsCancerDetectClicked(false);
    if (value === CONSUMER_VALUE) {
      selectConsumer();
    } else {
      selectOrg(Number(value.slice('org-'.length)));
    }
    queryClient.invalidateQueries();
    navigate(HOME_PATH);
  };

  return {
    canSwitch: options.length > 1,
    options,
    currentValue,
    currentLabel:
      options.find((o) => o.value === currentValue)?.label ?? '',
    title: isProUser ? 'Test Results' : 'My Tests',
    selectValue,
  };
};
